import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { rng, seedOf } from './noise.js';
import { footprints } from './site.js';

/**
 * The Sonoran desert, round a hotel in Scottsdale.
 *
 * Two kinds of place get plants, and only two, because a cactus you can walk
 * through is a lie about where you can walk:
 *
 *   - volumes the level already has: the palo verdes over the Caytons' cart
 *     path, the creosote beside it, and the golf course's three trees. Their
 *     boxes are hidden and the plant is drawn in the same space; a trunk that
 *     collided still collides, a branch that did not still does not.
 *   - outside the walls, where nobody goes: saguaro, palo verde, ocotillo,
 *     prickly pear, barrel cactus, creosote and rock, scattered from a fixed
 *     seed, seen over the lot wall and past the courts.
 */

const FT = 0.3048;
const m = (feet) => feet * FT;

function clean(list) {
  return list.map((g) => {
    const out = g.index ? g.toNonIndexed() : g;
    for (const name of Object.keys(out.attributes)) if (!['position', 'normal', 'uv'].includes(name)) out.deleteAttribute(name);
    if (!out.attributes.uv) out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(out.attributes.position.count * 2), 2));
    return out;
  });
}
const merge = (list) => mergeGeometries(clean(list));

/** A ribbed column, the way a cactus is: every other vertex round the rim pulled in. */
function ribbed(radius, height, ribs = 8) {
  const g = new THREE.CylinderGeometry(radius, radius * 1.05, height, ribs * 2, 1, false);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i += 1) {
    const x = p.getX(i);
    const z = p.getZ(i);
    const a = Math.atan2(z, x);
    const k = Math.round((a / (Math.PI * 2)) * ribs * 2) % 2 ? 0.86 : 1;
    p.setX(i, x * k);
    p.setZ(i, z * k);
  }
  g.computeVertexNormals();
  g.translate(0, height / 2, 0);
  return g;
}

/** A saguaro, about five metres tall before it is scaled, with `arms` arms. */
function saguaro(arms, seed) {
  const next = rng(seed);
  const parts = [];
  const height = 5;
  const r = 0.28;
  parts.push(ribbed(r, height));
  parts.push(new THREE.SphereGeometry(r, 12, 3, 0, Math.PI * 2, 0, Math.PI / 2).translate(0, height, 0));
  for (let i = 0; i < arms; i += 1) {
    const a = next() * Math.PI * 2;
    const y = 1.8 + next() * 1.6;
    const out = 0.55 + next() * 0.3;
    const up = 0.9 + next() * 1.4;
    const ar = r * 0.72;
    const elbow = new THREE.CylinderGeometry(ar, ar, out, 8).rotateZ(Math.PI / 2).translate(out / 2, y, 0).rotateY(a);
    const rise = ribbed(ar, up, 6).translate(out, y - ar * 0.5, 0).rotateY(a);
    const cap = new THREE.SphereGeometry(ar, 8, 3, 0, Math.PI * 2, 0, Math.PI / 2).translate(out, y - ar * 0.5 + up, 0).rotateY(a);
    parts.push(elbow, rise, cap);
  }
  return merge(parts);
}

/** A clump of leaves: small rough blobs in a dome of `radius`, `height` tall. */
function foliage(seed, count, radius, height, blob = 0.35, coarse = false) {
  const next = rng(seed);
  const parts = [];
  for (let i = 0; i < count; i += 1) {
    const a = next() * Math.PI * 2;
    const d = Math.sqrt(next()) * radius;
    const y = (1 - (d / radius) ** 2) * height * (0.5 + next() * 0.5);
    const s = blob * (0.6 + next() * 0.7);
    const shape = coarse ? new THREE.OctahedronGeometry(s, 0) : new THREE.IcosahedronGeometry(s, 0);
    parts.push(shape.scale(1, 0.7, 1).translate(Math.cos(a) * d, y, Math.sin(a) * d));
  }
  return merge(parts);
}

function pricklyPear(seed) {
  const next = rng(seed);
  const parts = [];
  const pad = (x, y, z, yaw, tilt, s) => new THREE.SphereGeometry(0.22 * s, 6, 4).scale(1, 1.2, 0.28).rotateZ(tilt).rotateY(yaw).translate(x, y, z);
  for (let i = 0; i < 5; i += 1) {
    const a = next() * Math.PI * 2;
    const x = Math.cos(a) * 0.25;
    const z = Math.sin(a) * 0.25;
    parts.push(pad(x, 0.22, z, a, (next() - 0.5) * 0.6, 1));
    parts.push(pad(x * 1.4, 0.58, z * 1.4, a + 0.6, (next() - 0.5) * 0.9, 0.9));
    if (next() > 0.4) parts.push(pad(x * 1.6, 0.9, z * 1.6, a - 0.5, (next() - 0.5) * 1.1, 0.75));
  }
  return merge(parts);
}

function ocotillo(seed) {
  const next = rng(seed);
  const parts = [];
  for (let i = 0; i < 12; i += 1) {
    const a = next() * Math.PI * 2;
    const lean = 0.12 + next() * 0.3;
    const len = 2.6 + next() * 1.6;
    parts.push(new THREE.CylinderGeometry(0.012, 0.035, len, 5).translate(0, len / 2, 0).rotateZ(lean).rotateY(a));
  }
  return merge(parts);
}

function rock(seed) {
  const next = rng(seed);
  const g = new THREE.DodecahedronGeometry(0.5, 0);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i += 1) p.setXYZ(i, p.getX(i) * (0.8 + next() * 0.4), p.getY(i) * (0.5 + next() * 0.3), p.getZ(i) * (0.8 + next() * 0.4));
  g.computeVertexNormals();
  return g;
}

/** A tree: a trunk that forks into limbs, with a canopy of leaf clusters on top. */
function tree({ seed, height, spread, trunk, lean = 0, coarse = false }) {
  const next = rng(seed);
  const wood = [];
  wood.push(new THREE.CylinderGeometry(trunk * 0.7, trunk, height * 0.45, 8).translate(0, height * 0.225, 0).rotateZ(lean));
  const limbs = [];
  for (let i = 0; i < 5; i += 1) {
    const a = (i / 5) * Math.PI * 2 + next() * 0.8;
    const len = height * (0.35 + next() * 0.2);
    const tilt = 0.6 + next() * 0.4;
    const g = new THREE.CylinderGeometry(trunk * 0.25, trunk * 0.5, len, 5).translate(0, len / 2, 0).rotateZ(tilt).rotateY(a).translate(0, height * 0.4, 0);
    wood.push(g);
    // Where the limb ends: tilted off +y toward -x, then turned about y.
    limbs.push({ x: -Math.cos(a) * Math.sin(tilt) * len * 0.9, y: height * 0.4 + Math.cos(tilt) * len, z: Math.sin(a) * Math.sin(tilt) * len * 0.9 });
  }
  const leaves = [];
  for (const [i, l] of limbs.entries()) {
    leaves.push(foliage(seed + i * 17, coarse ? 14 : 26, spread * 0.42, height * 0.22, spread * (coarse ? 0.07 : 0.05), coarse).translate(l.x, l.y - height * 0.05, l.z));
  }
  leaves.push(foliage(seed + 99, coarse ? 24 : 40, spread * 0.5, height * 0.28, spread * (coarse ? 0.075 : 0.055), coarse).translate(0, height * 0.72, 0));
  return { wood: merge(wood), leaves: merge(leaves) };
}

export function createVegetation({ plan, levelObject }) {
  const group = new THREE.Group();
  group.name = 'look-vegetation';
  const disposables = [];
  const mat = (colour, extra = {}) => {
    const made = new THREE.MeshStandardMaterial({ color: colour, roughness: 0.9, ...extra });
    disposables.push(made);
    return made;
  };
  const materials = {
    saguaro: mat(0x5f7a48),
    paloBark: mat(0x8a9a4e),
    paloLeaf: mat(0xb2bc66, { roughness: 1 }),
    mesquiteBark: mat(0x4a3a2c),
    mesquiteLeaf: mat(0x5a6a3a, { roughness: 1 }),
    creosote: mat(0x5a6634, { roughness: 1 }),
    pear: mat(0x6f8a4e),
    ocotillo: mat(0x5c5446),
    barrel: mat(0x5f7a40),
    rock: mat(0x8e7462),
  };
  const hide = (name) => {
    const mesh = levelObject.getObjectByName(name);
    if (mesh) mesh.visible = false;
  };
  const place = (geometry, material, x, y, z, yaw = 0, s = 1) => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.rotation.y = yaw;
    mesh.scale.setScalar(s);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    disposables.push(geometry);
    return mesh;
  };
  const rect = (r) => ({ minX: m(Math.min(r[0], r[2])), maxX: m(Math.max(r[0], r[2])), minZ: m(Math.min(r[1], r[3])), maxZ: m(Math.max(r[1], r[3])) });
  const centre = (r) => ({ x: (r.minX + r.maxX) / 2, z: (r.minZ + r.maxZ) / 2 });

  // --- the volumes the level already has ------------------------------------------
  const props = new Map(plan.props.map((p) => [p.id, p]));
  // Palo verdes: green-barked, a cloud of tiny yellow-green leaves, their low
  // branches reaching over the cart path exactly where the swipes are.
  for (const swipe of plan.swipes.filter((s) => s.kind === 'branch')) {
    const trunkId = swipe.id.replace('-branch', '-trunk');
    const trunkProp = props.get(trunkId);
    if (!trunkProp) continue;
    hide(`prop:${trunkId}`);
    hide(`swipe:${swipe.id}`);
    const t = centre(rect(trunkProp.rect));
    const sw = rect(swipe.rect);
    const height = m(15);
    const made = tree({ seed: seedOf(trunkId), height, spread: m(22), trunk: 0.16 });
    place(made.wood, materials.paloBark, t.x, 0, t.z);
    place(made.leaves, materials.paloLeaf, t.x, 0, t.z);
    // The low limb over the path, in the swipe's own volume.
    const s = centre(sw);
    const limbY = m(swipe.base + swipe.height / 2);
    const dx = s.x - t.x;
    const dz = s.z - t.z;
    const len = Math.hypot(dx, dz);
    const limb = new THREE.CylinderGeometry(0.05, 0.1, len, 6).rotateZ(Math.PI / 2 - 0.25).rotateY(Math.atan2(dz, -dx)).translate(dx / 2, limbY * 0.8, dz / 2);
    place(limb, materials.paloBark, t.x, 0, t.z);
    place(foliage(seedOf(swipe.id), 60, Math.max(sw.maxX - sw.minX, sw.maxZ - sw.minZ) / 2, m(swipe.height), 0.3), materials.paloLeaf, s.x, m(swipe.base), s.z);
  }
  // Creosote, in the bush swipes: dark, round, waist high.
  for (const swipe of plan.swipes.filter((s) => s.kind === 'bush')) {
    hide(`swipe:${swipe.id}`);
    const sw = rect(swipe.rect);
    const c = centre(sw);
    place(foliage(seedOf(swipe.id), 70, Math.max(sw.maxX - sw.minX, sw.maxZ - sw.minZ) / 2, m(swipe.base + swipe.height), 0.22), materials.creosote, c.x, 0, c.z);
  }
  // The golf course's trees: mesquite, dark and twisted, drawn in the space
  // their trunk and canopy boxes take.
  for (const trunk of plan.props.filter((p) => /^tree-.-trunk$/.test(p.id))) {
    const base = trunk.id.replace('-trunk', '');
    const canopy = props.get(`${base}-canopy`);
    hide(`prop:${trunk.id}`);
    hide(`prop:${base}-canopy`);
    hide(`prop:${base}-fork`);
    const t = centre(rect(trunk.rect));
    const top = canopy ? m(canopy.base + canopy.height) : m(trunk.height + 3);
    const spread = canopy ? Math.max(rect(canopy.rect).maxX - rect(canopy.rect).minX, rect(canopy.rect).maxZ - rect(canopy.rect).minZ) : m(12);
    const made = tree({ seed: seedOf(trunk.id), height: top, spread, trunk: 0.22, lean: 0.08 });
    const at = canopy ? centre(rect(canopy.rect)) : t;
    place(made.wood, materials.mesquiteBark, t.x, 0, t.z);
    place(made.leaves, materials.mesquiteLeaf, at.x, 0, at.z);
  }

  // --- outside the walls ---------------------------------------------------------
  const inside = plan.rooms.map((room) => rect(room.rect));
  const margin = 3;
  // And off the scenery: nothing grows through a car park, a building or a lake.
  const built = footprints(plan.scenery);
  const clear = (x, z) =>
    !inside.some((r) => x > r.minX - margin && x < r.maxX + margin && z > r.minZ - margin && z < r.maxZ + margin) &&
    !built.some((r) => x > r.minX && x < r.maxX && z > r.minZ && z < r.maxZ);
  const next = rng(seedOf('the desert'));
  /*
   * The whole site and a margin round it, in metres: the hotel north, the
   * Caytons two hundred and sixty metres west, the gatehouse two hundred south.
   * Counts below are sized to that ground, about two and a half times what
   * the old, smaller site had, so the desert is as thick as it was.
   */
  const AREA = { x0: -330, x1: 150, z0: -150, z1: 260 };
  const scatter = (count, minD = 0) => {
    const out = [];
    let tries = 0;
    while (out.length < count && tries < count * 40) {
      tries += 1;
      const x = AREA.x0 + next() * (AREA.x1 - AREA.x0);
      const z = AREA.z0 + next() * (AREA.z1 - AREA.z0);
      if (!clear(x, z)) continue;
      if (minD && out.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < minD * minD)) continue;
      out.push({ x, z, yaw: next() * Math.PI * 2, s: 0.7 + next() * 0.6 });
    }
    return out;
  };
  const dummy = new THREE.Object3D();
  /*
   * One instanced mesh per species per sixty-metre patch of desert, not one per
   * species: an instanced mesh is culled as a whole, and a mesh that spans the
   * whole desert is never behind the camera.
   */
  const PATCH = 60;
  const instanced = (geometry, material, points, scale = 1) => {
    if (!points.length) return;
    disposables.push(geometry);
    const patches = new Map();
    for (const p of points) {
      const key = `${Math.floor(p.x / PATCH)}|${Math.floor(p.z / PATCH)}`;
      if (!patches.has(key)) patches.set(key, []);
      patches.get(key).push(p);
    }
    for (const list of patches.values()) {
      const mesh = new THREE.InstancedMesh(geometry, material, list.length);
      list.forEach((p, i) => {
        dummy.position.set(p.x, -0.02, p.z);
        dummy.rotation.set(0, p.yaw, 0);
        dummy.scale.setScalar(p.s * scale);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      // Beyond the walls and mostly beyond the sun's shadow anyway: measured in
      // `perf.mjs`, drawing all of them into the shadow map as well doubled
      // what the desert cost.
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      group.add(mesh);
    }
  };

  const saguaros = scatter(150, 6);
  const kinds = [saguaro(0, 1), saguaro(2, 2), saguaro(3, 3), saguaro(1, 4)];
  kinds.forEach((g, k) => instanced(g, materials.saguaro, saguaros.filter((_, i) => i % kinds.length === k), 1.3));
  const verdes = scatter(45, 10);
  const verde = tree({ seed: 7, height: 6, spread: 7, trunk: 0.16, coarse: true });
  instanced(verde.wood, materials.paloBark, verdes);
  instanced(verde.leaves, materials.paloLeaf, verdes);
  instanced(pricklyPear(11), materials.pear, scatter(110));
  instanced(ocotillo(13), materials.ocotillo, scatter(75));
  instanced(ribbed(0.35, 0.7, 8), materials.barrel, scatter(110));
  instanced(foliage(17, 12, 0.9, 1.1, 0.34, true), materials.creosote, scatter(350));
  instanced(rock(19), materials.rock, scatter(300), 0.9);
  instanced(rock(23), materials.rock, scatter(150), 1.8);

  return {
    object3D: group,
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}
